import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { QRCodeCanvas } from 'qrcode.react';
import EventTimeline from '../../components/EventTimeline';
import { calculateTimeline } from '../../utils/timelineCalculator';
import { formatDietaryRestrictions } from '../../utils/dietaryUtils';
import './ManageEvent.css';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component to auto-fit map bounds
function MapBounds({ pairs, finalLocation }: { pairs: Pair[]; finalLocation?: { lat?: number; lon?: number } }) {
  const map = useMap();

  useEffect(() => {
    const validPairs = pairs.filter(p => p.lat && p.lon);
    const locations: [number, number][] = validPairs.map(p => [p.lat!, p.lon!]);
    
    // Include final location if available
    if (finalLocation?.lat && finalLocation?.lon) {
      locations.push([finalLocation.lat, finalLocation.lon]);
    }
    
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [pairs, finalLocation, map]);

  return null;
}

interface Event {
  id: string;
  name: string;
  description: string;
  scheduledDate: string;
  city: string;
  area: string;
  maxPairs: number;
  minPairs?: number;
  invitationCode: string;
  status: 'draft' | 'registration' | 'published' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  routeGenerated: boolean;
  organizerName?: string;
  organizerEmail?: string;
  organizerPhone?: string;
  finalGatheringLocation?: {
    name: string;
    address: string;
    lat?: number;
    lon?: number;
  };
  maxDistanceKm?: number;
  courses: {
    appetizer: boolean;
    mainCourse: boolean;
    dessert: boolean;
  };
  schedule?: {
    invitationCloseDaysBeforeEvent: number;
    invitationCloseTime?: string;
    mealAssignmentDaysBeforeEvent: number;
    mealAssignmentTime?: string;
    appetizerDuration: number;
    mainCourseDuration: number;
    dessertDuration: number;
    firstStopEmailMinutesBefore?: number;
  };
  routes?: any[];
  assignments?: any[];
  routesGeneratedAt?: string;
  hostEmailsSent?: boolean;
  pricePerEvent?: number;
  pricePerEventAccepted?: boolean;
  netDays?: number;
}

interface Participant {
  userId: string | null;
  name: string;
  email: string;
  phone: string;
  dietaryRestrictions: string | null;
  hostNotificationSent?: boolean;
  hostNotificationSentAt?: string;
}

interface Pair {
  id: string;
  eventId: string;
  participant1: Participant;
  participant2: Participant;
  address: string;
  lat?: number;
  lon?: number;
  preferredMeal: string;
  hostingMeal: string | null;
  hostDietaryCapabilities?: string[];
  registeredAt: string;
  notes: string | null;
  hostNotificationSent?: boolean;
  hostNotificationSentAt?: string;
}

const ManageEvent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [timeline, setTimeline] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHostNotificationConfirm, setShowHostNotificationConfirm] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const [showGenerateRoutesModal, setShowGenerateRoutesModal] = useState(false);
  const [generateRoutesLoading, setGenerateRoutesLoading] = useState(false);
  const [routeErrors, setRouteErrors] = useState<string[]>([]);
  const [routeWarnings, setRouteWarnings] = useState<string[]>([]);
  const [hostNotificationsLoading, setHostNotificationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'pairs' | 'routes' | 'timeline' | 'settings' | 'logs'>('overview');
  const [dietaryOptionsMap, setDietaryOptionsMap] = useState<{ [key: string]: string }>({});
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [logSubTab, setLogSubTab] = useState<'emails' | 'system'>('emails');
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsMessage, setStatisticsMessage] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const FRONTEND_URL = window.location.origin;

  useEffect(() => {
    fetchDietaryOptions();
  }, []);

  useEffect(() => {
    if (id && accessToken) {
      fetchEvent();
      fetchPairs();
    }
  }, [id, accessToken]);

  useEffect(() => {
    // Calculate timeline when event is loaded
    if (event?.scheduledDate && event?.schedule) {
      const timelineData = calculateTimeline(event.scheduledDate, event.schedule);
      setTimeline(timelineData);
    }
  }, [event]);

  useEffect(() => {
    // Fetch logs when logs tab is active
    if (activeTab === 'logs' && id && accessToken) {
      fetchLogs();
    }
  }, [activeTab, id, accessToken]);

  const fetchDietaryOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/settings`);
      if (response.data.dietaryOptions) {
        const map: { [key: string]: string } = {};
        response.data.dietaryOptions.forEach((option: any) => {
          map[option.value] = option.label;
        });
        setDietaryOptionsMap(map);
      }
    } catch (err) {
      console.error('Error fetching dietary options:', err);
      // Don't block the UI if this fails
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizers/events/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setEvent(response.data);
      
      // Fetch routes if they've been generated
      if (response.data.routeGenerated) {
        fetchRoutes();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente arrangement');
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizers/events/${id}/routes`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      console.log('📍 fetchRoutes response:', response.data);
      
      if (response.data.routes && response.data.routes.length > 0) {
        console.log('✅ Updating routes with', response.data.routes.length, 'routes');
        setEvent(prev => prev ? {
          ...prev,
          routes: response.data.routes,
          assignments: response.data.assignments,
          routesGeneratedAt: response.data.generatedAt
        } : null);
      } else {
        console.log('⚠️ No routes found in response:', response.data);
      }
    } catch (err: any) {
      console.error('❌ Error fetching routes:', err);
      // Don't set error, routes are optional
    }
  };

  const fetchPairs = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizers/events/${id}/pairs`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setPairs(response.data.pairs || []);
    } catch (err: any) {
      console.error('Error fetching pairs:', err);
      // Don't set error, pairs are optional
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      console.log('Fetching logs for event:', id);
      const response = await axios.get(`${API_URL}/organizers/events/${id}/logs`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 10000, // 10 second timeout
      });
      console.log('Logs response:', response.data);
      setLogs(response.data || []);
    } catch (err: any) {
      console.error('Error fetching logs:', err);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      // Don't show error to user if it's a 404 (no logs yet) or 500 (backend not ready)
      // Just set empty array and let them try again
      if (err.code === 'ECONNABORTED') {
        console.log('Request timed out - logs endpoint may be slow');
      }
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const normalizeDietaryOptions = (options: string[]): string => {
    if (!options || options.length === 0) return '';
    return options
      .map(option => dietaryOptionsMap[option] || option)
      .join(', ');
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/organizers/events/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      navigate('/arrangor/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Kunne ikke slette arrangement';
      setError(errorMessage);
      alert(errorMessage);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!cancelMessage.trim()) {
      alert('Vennligst skriv en melding til deltakerne');
      return;
    }

    setCancelLoading(true);
    try {
      await axios.post(
        `${API_URL}/organizers/events/${id}/cancel`,
        { message: cancelMessage },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert('Arrangementet er avlyst og deltakerne er varslet');
      navigate('/arrangor/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke avlyse arrangement');
      setShowCancelModal(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageSubject.trim()) {
      alert('Vennligst skriv en emnetittel');
      return;
    }

    if (!messageContent.trim()) {
      alert('Vennligst skriv en melding til deltakerne');
      return;
    }

    setMessageLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/organizers/events/${id}/message`,
        { subject: messageSubject, message: messageContent },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      alert(`Meldingen er sendt til ${response.data.emailsSent} deltakere!`);
      setShowMessageModal(false);
      setMessageSubject('');
      setMessageContent('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke sende melding');
      alert(err.response?.data?.message || 'Kunne ikke sende melding');
    } finally {
      setMessageLoading(false);
    }
  };

  const handleGenerateRoutes = async () => {
    setGenerateRoutesLoading(true);
    setRouteErrors([]);
    setRouteWarnings([]);

    try {
      const response = await axios.post(
        `${API_URL}/organizers/events/${id}/generate-routes`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setRouteWarnings(response.data.warnings || []);
      
      // Refresh event and routes
      await fetchEvent();
      await fetchRoutes();
      
      alert('Rutene er generert! Du kan nå se dem under "Ruter"-seksjonen.');
      setShowGenerateRoutesModal(false);
    } catch (err: any) {
      const errorData = err.response?.data;
      setRouteErrors(errorData?.errors || [errorData?.message || 'Kunne ikke generere ruter']);
      setRouteWarnings(errorData?.warnings || []);
    } finally {
      setGenerateRoutesLoading(false);
    }
  };

  const handleSendHostNotifications = () => {
    setShowHostNotificationConfirm(true);
  };

  const handleConfirmHostNotifications = async () => {
    setHostNotificationsLoading(true);
    setShowHostNotificationConfirm(false);
    
    try {
      const response = await axios.post(
        `${API_URL}/organizers/events/${id}/notify-hosts`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      alert(`${response.data.message}! Vertene har mottatt informasjon om deres måltid.`);
      
      // Refresh logs if logs tab is active
      if (activeTab === 'logs') {
        await fetchLogs();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Kunne ikke sende varsler til vertene');
      
      // Refresh logs even on error to show what happened
      if (activeTab === 'logs') {
        await fetchLogs();
      }
    } finally {
      setHostNotificationsLoading(false);
    }
  };

  const copyInvitationLink = () => {
    const invitationLink = `${FRONTEND_URL}/bli-med?code=${event?.invitationCode}`;
    navigator.clipboard.writeText(invitationLink).then(() => {
      setCopySuccess('Lenke kopiert!');
      setTimeout(() => setCopySuccess(''), 3000);
    }).catch(() => {
      setCopySuccess('Kunne ikke kopiere');
    });
  };

  const copyInvitationCode = () => {
    navigator.clipboard.writeText(event?.invitationCode || '').then(() => {
      setCopySuccess('Kode kopiert!');
      setTimeout(() => setCopySuccess(''), 3000);
    }).catch(() => {
      setCopySuccess('Kunne ikke kopiere');
    });
  };

  const copyQRCode = async () => {
    try {
      const canvas = qrCodeRef.current?.querySelector('canvas');
      if (!canvas) return;
      
      canvas.toBlob((blob) => {
        if (blob) {
          const item = new ClipboardItem({ 'image/png': blob });
          navigator.clipboard.write([item]).then(() => {
            setCopySuccess('QR-kode kopiert!');
            setTimeout(() => setCopySuccess(''), 3000);
          }).catch(() => {
            setCopySuccess('Kunne ikke kopiere QR-kode');
          });
        }
      });
    } catch (err) {
      setCopySuccess('Kunne ikke kopiere QR-kode');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const downloadQRCode = () => {
    try {
      const canvas = qrCodeRef.current?.querySelector('canvas');
      if (!canvas) return;
      
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `sykkelfest-${event?.invitationCode}-qr.png`;
      link.href = url;
      link.click();
      
      setCopySuccess('QR-kode lastet ned!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      setCopySuccess('Kunne ikke laste ned QR-kode');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const copyLogo = async () => {
    try {
      const response = await fetch('/logo.png');
      const blob = await response.blob();
      const item = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([item]);
      setCopySuccess('Logo kopiert!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      setCopySuccess('Kunne ikke kopiere logo');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const downloadLogo = () => {
    const link = document.createElement('a');
    link.download = 'sykkelfest-logo.png';
    link.href = '/logo.png';
    link.click();
    
    setCopySuccess('Logo lastet ned!');
    setTimeout(() => setCopySuccess(''), 3000);
  };

  const handleGenerateStatistics = async () => {
    if (!id) return;
    
    setStatisticsLoading(true);
    setStatisticsMessage('');
    
    try {
      const response = await axios.post(
        `${API_URL}/statistics/generate/${id}`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      setStatisticsMessage('Statistikk generert!');
      setTimeout(() => setStatisticsMessage(''), 5000);
    } catch (err: any) {
      setStatisticsMessage(err.response?.data?.message || 'Kunne ikke generere statistikk');
      setTimeout(() => setStatisticsMessage(''), 5000);
    } finally {
      setStatisticsLoading(false);
    }
  };

  if (loading) {
    return <div className="manage-event-page"><p>Laster...</p></div>;
  }

  if (error || !event) {
    return (
      <div className="manage-event-page">
        <div className="error-message">{error || 'Arrangement ikke funnet'}</div>
        <Link to="/arrangor/dashboard" className="btn btn-secondary">
          Tilbake til dashboard
        </Link>
      </div>
    );
  }

  const courseCount = [event.courses.appetizer, event.courses.mainCourse, event.courses.dessert]
    .filter(Boolean).length;
  const maxParticipants = event.maxPairs * 2;

  return (
    <div className="manage-event-page">
      <div className="manage-event-container">
        <div className="page-header">
          <h1>{event.name}</h1>
          <div className="header-actions">
            <Link to="/arrangor/dashboard" className="btn btn-secondary">
              ← Tilbake
            </Link>
          </div>
        </div>

        <div className="event-overview">
          {/* Tabbed Section */}
          <div className="tabbed-section">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                📊 Oversikt
              </button>
              <button 
                className={`tab ${activeTab === 'pairs' ? 'active' : ''}`}
                onClick={() => setActiveTab('pairs')}
              >
                👥 Påmeldte par ({pairs.length})
              </button>
              <button 
                className={`tab ${activeTab === 'routes' ? 'active' : ''}`}
                onClick={() => setActiveTab('routes')}
              >
                🚴 Ruter {event.routeGenerated ? '✅' : ''}
              </button>
              <button 
                className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeline')}
              >
                📅 Tidsplan
              </button>
              <button 
                className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                ⚙️ Innstillinger
              </button>
              <button 
                className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
                onClick={() => setActiveTab('logs')}
              >
                📋 Logg
              </button>
            </div>

            <div className="tab-content">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <>
          <div className="overview-section">
            <h2>Oversikt</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`status-badge status-${event.status}`}>
                  {event.status === 'registration' && '📝 Påmelding åpen'}
                  {event.status === 'confirmed' && '✅ Bekreftet'}
                  {event.status === 'draft' && '✏️ Utkast'}
                  {event.status === 'published' && '✅ Publisert'}
                  {event.status === 'completed' && '🏁 Fullført'}
                  {event.status === 'cancelled' && '🚫 Avlyst'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Invitasjonskode:</span>
                <div className="invitation-code-container">
                  <span className="invitation-code">{event.invitationCode}</span>
                  <button onClick={copyInvitationCode} className="btn-copy" title="Kopier kode">
                    📋
                  </button>
                </div>
              </div>
              <div className="info-item full-width">
                <span className="info-label">Invitasjonslenke:</span>
                <div className="invitation-link-container">
                  <input 
                    type="text" 
                    value={`${FRONTEND_URL}/bli-med?code=${event.invitationCode}`}
                    readOnly 
                    className="invitation-link-input"
                  />
                  <button onClick={copyInvitationLink} className="btn btn-primary btn-small">
                    Kopier lenke
                  </button>
                </div>
                {copySuccess && <span className="copy-success">{copySuccess}</span>}
                <p className="info-hint">
                  Del denne lenken med deltakere for enklere påmelding - de slipper å skrive inn koden manuelt.
                </p>
              </div>
              <div className="info-item full-width">
                <span className="info-label">QR-kode for invitasjonslenke:</span>
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div ref={qrCodeRef} style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '4px' }}>
                    <QRCodeCanvas 
                      value={`${FRONTEND_URL}/bli-med?code=${event.invitationCode}`}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={copyQRCode} className="btn btn-secondary btn-small">
                      📋 Kopier QR-kode
                    </button>
                    <button onClick={downloadQRCode} className="btn btn-primary btn-small">
                      💾 Last ned QR-kode
                    </button>
                  </div>
                  <p className="info-hint" style={{ margin: 0, textAlign: 'center' }}>
                    Bruk QR-koden på plakater, flyers eller i sosiale medier for enkel påmelding.
                  </p>
                </div>
              </div>
              <div className="info-item full-width">
                <span className="info-label">Sykkelfest-logo:</span>
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '4px', maxWidth: '300px' }}>
                    <img 
                      src="/logo.png" 
                      alt="Sykkelfest logo" 
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={copyLogo} className="btn btn-secondary btn-small">
                      📋 Kopier logo
                    </button>
                    <button onClick={downloadLogo} className="btn btn-primary btn-small">
                      💾 Last ned logo
                    </button>
                  </div>
                  <p className="info-hint" style={{ margin: 0, textAlign: 'center' }}>
                    Bruk logoen på markedsføringsmateriell, plakater og i sosiale medier.
                  </p>
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Dato:</span>
                <span>{new Date(event.scheduledDate).toLocaleDateString('nb-NO', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Område:</span>
                <span>{event.city}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Maks deltakere:</span>
                <span>{maxParticipants} ({event.maxPairs} par)</span>
              </div>
              <div className="info-item">
                <span className="info-label">Retter:</span>
                <span>{courseCount} retter</span>
              </div>
              {event.finalGatheringLocation && (
                <div className="info-item">
                  <span className="info-label">Sluttdestinasjon:</span>
                  <span>{event.finalGatheringLocation.address}</span>
                </div>
              )}
              {event.organizerName && event.organizerEmail && (
                <>
                  <div className="info-item">
                    <span className="info-label">Arrangør:</span>
                    <span>{event.organizerName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">E-post:</span>
                    <span><a href={`mailto:${event.organizerEmail}`}>{event.organizerEmail}</a></span>
                  </div>
                  {event.organizerPhone && (
                    <div className="info-item">
                      <span className="info-label">Telefon:</span>
                      <span><a href={`tel:${event.organizerPhone}`}>{event.organizerPhone}</a></span>
                    </div>
                  )}
                </>
              )}
              {event.pricePerEvent && (
                <>
                  <div className="info-item">
                    <span className="info-label">Pris:</span>
                    <span>{event.pricePerEvent.toLocaleString('nb-NO')} kr</span>
                  </div>
                  {event.netDays && (
                    <div className="info-item">
                      <span className="info-label">Betalingsbetingelser:</span>
                      <span>Netto {event.netDays} dager</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {event.description && (
            <div className="overview-section">
              <h2>Beskrivelse</h2>
              <p>{event.description}</p>
            </div>
          )}

          <div className="overview-section">
            <h2>Retter</h2>
            <div className="courses-list">
              {event.courses.appetizer && <span className="course-badge">🥗 Forrett</span>}
              {event.courses.mainCourse && <span className="course-badge">🍽️ Hovedrett</span>}
              {event.courses.dessert && <span className="course-badge">🍰 Dessert</span>}
            </div>
          </div>
                </>
              )}

              {/* Påmeldte Par Tab */}
              {activeTab === 'pairs' && (
                <div className="overview-section">
                  <h2>Påmeldte par</h2>
                  {pairs.length === 0 ? (
                    <p>Ingen påmeldte par ennå</p>
                  ) : (
                    <>
                      {/* Pair Count and Status */}
                      <div className="pair-count-info" style={{ marginBottom: '24px', marginTop: 0 }}>
                        <p className="pair-count" style={{ fontSize: '18px', marginBottom: '12px' }}>
                          <strong>{pairs.length} av {event.maxPairs} par påmeldt</strong>
                        </p>
                        
                        {/* Route Calculation Requirements */}
                        {pairs.length % 3 !== 0 ? (
                          <div style={{ 
                            marginBottom: '12px', 
                            padding: '12px', 
                            borderRadius: '8px',
                            backgroundColor: '#FEE2E2',
                            border: '2px solid #EF4444'
                          }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#991B1B' }}>
                              ❌ Antall par må være delelig med 3 for rutegenerering
                            </p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#991B1B' }}>
                              Du har {pairs.length} par. Du må ha 9, 12, 15, 18, 21, 24, 27, 30, osv. par.
                              {pairs.length < 9 ? ` Legg til ${9 - pairs.length} par til for å få 9 par.` :
                               pairs.length < 12 ? ` Legg til ${12 - pairs.length} par til (12 par) eller fjern ${pairs.length - 9} par (9 par).` :
                               pairs.length < 15 ? ` Legg til ${15 - pairs.length} par til (15 par) eller fjern ${pairs.length - 12} par (12 par).` :
                               ` Legg til ${3 - (pairs.length % 3)} par til eller fjern ${pairs.length % 3} par.`}
                            </p>
                          </div>
                        ) : (
                          <div style={{ 
                            marginBottom: '12px', 
                            padding: '12px', 
                            borderRadius: '8px',
                            backgroundColor: '#D1FAE5',
                            border: '2px solid #10B981'
                          }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#065F46' }}>
                              ✅ Antall par er delelig med 3 - klar for rutegenerering!
                            </p>
                          </div>
                        )}

                        {event.minPairs && (
                          <div style={{ 
                            padding: '12px', 
                            borderRadius: '8px',
                            backgroundColor: pairs.length >= event.minPairs ? '#D1FAE5' : '#FEF3C7',
                            border: `2px solid ${pairs.length >= event.minPairs ? '#10B981' : '#F59E0B'}`
                          }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: pairs.length >= event.minPairs ? '#065F46' : '#92400E' }}>
                              {pairs.length >= event.minPairs ? '✅' : '⚠️'} Minimum antall par: {event.minPairs}
                            </p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: pairs.length >= event.minPairs ? '#065F46' : '#92400E' }}>
                              {pairs.length >= event.minPairs 
                                ? `Kravet er oppfylt! Med ${courseCount} retter trenger du minst ${event.minPairs} par for at alle møter nye par på hver rett.`
                                : `Du trenger ${event.minPairs - pairs.length} par til. Med ${courseCount} retter må du ha minst ${event.minPairs} par for at alle møter nye par på hver rett.`
                              }
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Map with pair locations */}
                      {pairs.some(p => p.lat && p.lon) && (
                        <div style={{ marginBottom: '24px' }}>
                          <h3 style={{ marginBottom: '12px' }}>📍 Oversikt over parrenes adresser</h3>
                          <MapContainer
                            center={[60.0, 10.0]}
                            zoom={11}
                            style={{ height: '400px', width: '100%', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapBounds pairs={pairs} finalLocation={event?.finalGatheringLocation} />
                            
                            {/* Final gathering location with radius */}
                            {event?.finalGatheringLocation?.lat && event?.finalGatheringLocation?.lon && (
                              <>
                                {/* Radius circle showing max distance */}
                                {event.maxDistanceKm && (
                                  <Circle
                                    center={[event.finalGatheringLocation.lat, event.finalGatheringLocation.lon]}
                                    radius={event.maxDistanceKm * 1000} // Convert km to meters
                                    pathOptions={{
                                      color: '#FF6B6B',
                                      fillColor: '#FF6B6B',
                                      fillOpacity: 0.1,
                                      weight: 2,
                                      dashArray: '5, 10'
                                    }}
                                  />
                                )}
                                
                                {/* Final location marker */}
                                <Marker 
                                  position={[event.finalGatheringLocation.lat, event.finalGatheringLocation.lon]}
                                  icon={L.icon({
                                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                                    iconSize: [25, 41],
                                    iconAnchor: [12, 41],
                                    popupAnchor: [1, -34],
                                    shadowSize: [41, 41]
                                  })}
                                >
                                  <Popup>
                                    <div style={{ minWidth: '200px' }}>
                                      <strong style={{ fontSize: '16px', color: '#DC2626' }}>🎉 Avslutningsfest</strong><br/>
                                      <br/>
                                      <strong>📍 Sted:</strong><br/>
                                      {event.finalGatheringLocation?.name}<br/>
                                      {event.finalGatheringLocation?.address}<br/>
                                      <br/>
                                      {event.maxDistanceKm && (
                                        <>
                                          <strong>📏 Maks avstand fra senter:</strong><br/>
                                          {event.maxDistanceKm} km<br/>
                                          <br/>
                                        </>
                                      )}
                                      <strong>👤 Arrangør:</strong><br/>
                                      {event.organizerName}<br/>
                                      {event.organizerEmail}<br/>
                                      {event.organizerPhone}
                                    </div>
                                  </Popup>
                                </Marker>
                              </>
                            )}
                            
                            {/* Pair locations */}
                            {pairs
                              .filter(p => p.lat && p.lon)
                              .map((pair, index) => (
                                <Marker 
                                  key={pair.id} 
                                  position={[pair.lat!, pair.lon!]}
                                >
                                  <Popup>
                                    <div style={{ minWidth: '200px' }}>
                                      <strong>Par #{pairs.indexOf(pair) + 1}</strong><br/>
                                      <strong>👤 {pair.participant1.name}</strong><br/>
                                      <strong>👤 {pair.participant2.name}</strong><br/>
                                      <br/>
                                      <strong>📍 Adresse:</strong><br/>
                                      {pair.address}<br/>
                                      <br/>
                                      {pair.hostingMeal && (
                                        <>
                                          <strong>✅ Tildelt rett:</strong><br/>
                                          {pair.hostingMeal === 'appetizer' ? 'Forrett' :
                                           pair.hostingMeal === 'mainCourse' ? 'Hovedrett' : 'Dessert'}<br/>
                                          <br/>
                                        </>
                                      )}
                                      {pair.preferredMeal && (
                                        <>
                                          <strong>🥘 Ønsker:</strong> {
                                            pair.preferredMeal === 'appetizer' ? 'Forrett' :
                                            pair.preferredMeal === 'mainCourse' ? 'Hovedrett' : 'Dessert'
                                          }
                                        </>
                                      )}
                                    </div>
                                  </Popup>
                                </Marker>
                              ))}
                          </MapContainer>
                        </div>
                      )}

                      <div className="pairs-list">
                      {pairs.map((pair) => (
                        <div key={pair.id} className="pair-card">
                          <div className="pair-header">
                            <h3>Par #{pairs.indexOf(pair) + 1}</h3>
                            <span className="pair-date">
                              Påmeldt: {new Date(pair.registeredAt).toLocaleDateString('nb-NO')}
                            </span>
                          </div>
                          <div className="pair-participants">
                            <div className="participant">
                              <strong>👤 {pair.participant1.name}</strong>
                              <div className="participant-details">
                                <span>📧 {pair.participant1.email}</span>
                                <span>📱 {pair.participant1.phone}</span>
                                {formatDietaryRestrictions(pair.participant1.dietaryRestrictions) && (
                                  <span className="dietary-info">⚠️ Diett/allergier: {formatDietaryRestrictions(pair.participant1.dietaryRestrictions)}</span>
                                )}
                              </div>
                            </div>
                            <div className="participant">
                              <strong>👤 {pair.participant2.name}</strong>
                              <div className="participant-details">
                                <span>📧 {pair.participant2.email}</span>
                                <span>📱 {pair.participant2.phone}</span>
                                {formatDietaryRestrictions(pair.participant2.dietaryRestrictions) && (
                                  <span className="dietary-info">⚠️ Diett/allergier: {formatDietaryRestrictions(pair.participant2.dietaryRestrictions)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="pair-info">
                            <p><strong>📍 Adresse:</strong> {pair.address}</p>
                            {pair.preferredMeal && (
                              <p><strong>🥘 Foretrukket rett:</strong> {
                                pair.preferredMeal === 'appetizer' ? 'Forrett' :
                                pair.preferredMeal === 'mainCourse' ? 'Hovedrett' : 'Dessert'
                              }</p>
                            )}
                            {pair.hostDietaryCapabilities && pair.hostDietaryCapabilities.length > 0 && (
                              <p><strong>🍽️ Kan tilby kosthold:</strong> {formatDietaryRestrictions(pair.hostDietaryCapabilities)}</p>
                            )}
                            {pair.hostingMeal && (
                              <p><strong>✅ Tildelt rett:</strong> {
                                pair.hostingMeal === 'appetizer' ? 'Forrett' :
                                pair.hostingMeal === 'mainCourse' ? 'Hovedrett' : 'Dessert'
                              }</p>
                            )}
                            {pair.notes && (
                              <p><strong>📝 Notater:</strong> {pair.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    </>
                  )}
                </div>
              )}

              {/* Ruter Tab */}
              {activeTab === 'routes' && (
                <div className="overview-section">
                  <h2>Ruter</h2>
                  {event.routeGenerated && event.routes && event.routes.length > 0 ? (
                    <div>
                      {event.routesGeneratedAt && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <p style={{ fontSize: '14px', color: '#065F46', fontWeight: '600', margin: 0 }}>
                              Ruter generert {new Date(event.routesGeneratedAt).toLocaleString('nb-NO')}
                            </p>
                            {(() => {
                              // Calculate host notification status (participant-level)
                              const assignments = event.assignments || [];
                              const hostAssignments = assignments.filter((a: any) => a.role === 'host');
                              
                              let totalParticipants = 0;
                              let notifiedParticipants = 0;
                              
                              hostAssignments.forEach((a: any) => {
                                const pair = pairs.find(p => p.id === a.pairId);
                                if (pair) {
                                  if (pair.participant1?.email) {
                                    totalParticipants++;
                                    if (pair.participant1.hostNotificationSent) notifiedParticipants++;
                                  }
                                  if (pair.participant2?.email) {
                                    totalParticipants++;
                                    if (pair.participant2.hostNotificationSent) notifiedParticipants++;
                                  }
                                }
                              });
                              
                              if (totalParticipants > 0) {
                                const allNotified = notifiedParticipants === totalParticipants;
                                return (
                                  <p style={{ 
                                    fontSize: '13px', 
                                    color: allNotified ? '#065F46' : '#92400E', 
                                    fontWeight: '500', 
                                    margin: 0 
                                  }}>
                                    {allNotified ? '✅' : '⏳'} Verter varslet: {notifiedParticipants}/{totalParticipants}
                                  </p>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          <button 
                            className="btn btn-primary" 
                            onClick={handleSendHostNotifications}
                            disabled={hostNotificationsLoading}
                          >
                            {hostNotificationsLoading ? '📧 Sender...' : '📧 Varsle verter'}
                          </button>
                        </div>
                      )}

                      <div style={{ marginTop: '20px' }}>
                        <h3 style={{ marginBottom: '16px' }}>📊 Ruteoversikt ({event.routes.length} par)</h3>
                        <div style={{ overflowX: 'auto' }}>
                          {event.routes.map((route: any, idx: number) => {
                            const pair = pairs.find(p => p.id === route.pairId);
                            if (!pair) return null;

                            return (
                              <div key={route.pairId} style={{ 
                                marginBottom: '20px', 
                                padding: '16px', 
                                border: '1px solid #E5E7EB', 
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                                  <div>
                                    <h4 style={{ margin: '0 0 4px 0', color: '#111827' }}>
                                      Par #{idx + 1}: {pair.participant1.name} & {pair.participant2.name}
                                    </h4>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                                      📍 {pair.address}
                                    </p>
                                  </div>
                                  <div style={{ 
                                    padding: '4px 12px', 
                                    backgroundColor: '#DBEAFE', 
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    color: '#1E40AF'
                                  }}>
                                    Total: {route.totalDistance} km
                                  </div>
                                </div>

                                <div style={{ marginTop: '12px' }}>
                                  {route.stops.map((stop: any, stopIdx: number) => {
                                    const isParty = stop.course === 'party';
                                    const isHosting = !isParty && stop.hostPairId === route.pairId;
                                    const hostPair = !isParty ? pairs.find(p => p.id === stop.hostPairId) : null;
                                    
                                    // Get all guests for this stop (including the current pair if they're a guest)
                                    const otherGuestPairs = !isParty ? (stop.guestPairIds || []).map((guestId: string) => 
                                      pairs.find(p => p.id === guestId)
                                    ).filter(Boolean) : [];
                                    
                                    // Add current pair to guest list if they're not hosting
                                    const allGuestPairs = isHosting 
                                      ? otherGuestPairs 
                                      : [...otherGuestPairs, pair];

                                    return (
                                      <div key={stopIdx} style={{ 
                                        marginBottom: '12px', 
                                        paddingLeft: '16px', 
                                        borderLeft: isParty ? '3px solid #EC4899' : '3px solid #3B82F6',
                                        paddingTop: '8px',
                                        paddingBottom: '8px',
                                        backgroundColor: isParty ? '#FDF2F8' : (isHosting ? '#F0FDF4' : 'transparent')
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '18px' }}>
                                            {stop.course === 'appetizer' && '🥗'}
                                            {stop.course === 'mainCourse' && '🍽️'}
                                            {stop.course === 'dessert' && '🍰'}
                                            {stop.course === 'party' && '🎉'}
                                          </span>
                                          <strong style={{ color: '#111827', fontSize: '15px' }}>
                                            {stop.course === 'appetizer' && 'Forrett'}
                                            {stop.course === 'mainCourse' && 'Hovedrett'}
                                            {stop.course === 'dessert' && 'Dessert'}
                                            {stop.course === 'party' && 'Etterfest'}
                                          </strong>
                                          {isHosting && (
                                            <span style={{ 
                                              padding: '2px 8px', 
                                              backgroundColor: '#10B981', 
                                              color: 'white',
                                              borderRadius: '8px',
                                              fontSize: '11px',
                                              fontWeight: 'bold'
                                            }}>
                                              VERT
                                            </span>
                                          )}
                                          {stop.distance > 0 && (
                                            <span style={{ fontSize: '12px', color: '#6B7280' }}>
                                              🚴 {stop.distance} km
                                            </span>
                                          )}
                                        </div>
                                        
                                        <div style={{ fontSize: '13px', color: '#374151', marginLeft: '26px', lineHeight: '1.6' }}>
                                          {!isParty && hostPair && (
                                            <>
                                              <div style={{ marginBottom: '4px' }}>
                                                <strong>Verter:</strong>
                                                <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                                    <span>{hostPair.participant1.name}</span>
                                                    {isHosting && hostPair.participant1.hostNotificationSent && (
                                                      <span 
                                                        style={{ 
                                                          padding: '1px 6px', 
                                                          backgroundColor: '#065F46', 
                                                          color: 'white',
                                                          borderRadius: '6px',
                                                          fontSize: '10px',
                                                          fontWeight: 'bold'
                                                        }}
                                                        title={`${hostPair.participant1.name} varslet ${new Date(hostPair.participant1.hostNotificationSentAt!).toLocaleString('nb-NO')}`}
                                                      >
                                                        ✉️
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span>{hostPair.participant2.name}</span>
                                                    {isHosting && hostPair.participant2.hostNotificationSent && (
                                                      <span 
                                                        style={{ 
                                                          padding: '1px 6px', 
                                                          backgroundColor: '#065F46', 
                                                          color: 'white',
                                                          borderRadius: '6px',
                                                          fontSize: '10px',
                                                          fontWeight: 'bold'
                                                        }}
                                                        title={`${hostPair.participant2.name} varslet ${new Date(hostPair.participant2.hostNotificationSentAt!).toLocaleString('nb-NO')}`}
                                                      >
                                                        ✉️
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              <div style={{ marginBottom: '4px', fontSize: '12px', color: '#6B7280' }}>
                                                📍 {stop.hostAddress}
                                              </div>
                                              {allGuestPairs.length > 0 && (
                                                <div style={{ marginTop: '6px' }}>
                                                  {allGuestPairs.map((guestPair: any, idx: number) => (
                                                    <div key={idx} style={{ marginBottom: '2px' }}>
                                                      <strong>Gjest {idx + 1}:</strong> {guestPair.participant1.name} & {guestPair.participant2.name}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </>
                                          )}
                                          {isParty && (
                                            <div style={{ marginBottom: '4px', fontSize: '12px', color: '#6B7280' }}>
                                              📍 {stop.hostAddress}
                                            </div>
                                          )}
                                          {!isParty && stop.hostDietaryCapabilities && stop.hostDietaryCapabilities.length > 0 && (
                                            <div style={{ 
                                              marginTop: '8px', 
                                              padding: '6px 8px', 
                                              backgroundColor: '#DBEAFE', 
                                              border: '1px solid #3B82F6',
                                              borderRadius: '4px',
                                              fontSize: '12px'
                                            }}>
                                              <strong>✅ Verten kan tilby:</strong> {normalizeDietaryOptions(stop.hostDietaryCapabilities)}
                                            </div>
                                          )}
                                          {stop.dietaryRestrictions && stop.dietaryRestrictions.length > 0 && (
                                            <div style={{ 
                                              marginTop: '8px', 
                                              padding: '6px 8px', 
                                              backgroundColor: '#FEF3C7', 
                                              border: '1px solid #F59E0B',
                                              borderRadius: '4px',
                                              fontSize: '12px'
                                            }}>
                                              <strong>⚠️ Diett/allergier:</strong> {normalizeDietaryOptions(stop.dietaryRestrictions)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : event.routeGenerated ? (
                    <div>
                      <p className="success-message">✅ Ruter er generert (men ingen data tilgjengelig)</p>
                    </div>
                  ) : (
                    <div>
                      <p>Ruter er ikke generert ennå. Gå til <strong>Innstillinger</strong> for å generere ruter.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Tidsplan Tab */}
              {activeTab === 'timeline' && timeline && (
                <div className="overview-section">
                  <h2>Tidsplan for sykkelfesten {event.name}</h2>
                  <EventTimeline eventName={event.name} eventDate={event.scheduledDate} timeline={timeline} />
                </div>
              )}

              {/* Innstillinger Tab */}
              {activeTab === 'settings' && (
                <div className="overview-section">
                  <h2>Innstillinger</h2>
                  
                  {/* Route Generation Section */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ marginBottom: '16px' }}>🚴 Rutegenerering</h3>
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#F9FAFB', 
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB'
                    }}>
                      {event.routeGenerated ? (
                        <>
                          <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#065F46' }}>
                            ✅ Ruter er generert
                          </h4>
                          {event.routesGeneratedAt && (
                            <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                              Ruter skal nå genereres automatisk. Generert: {new Date(event.routesGeneratedAt).toLocaleString('nb-NO')}
                            </p>
                          )}
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => setShowGenerateRoutesModal(true)}
                          >
                            🔄 Generer ruter på nytt
                          </button>
                        </>
                      ) : (
                        <>
                          <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#111827' }}>
                            Generer ruter for sykkelfesten
                          </h4>
                          {pairs.length === 0 ? (
                            <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                              Du må ha påmeldte par før du kan generere ruter.
                            </p>
                          ) : event.minPairs && pairs.length < event.minPairs ? (
                            <>
                              <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                                Du har {pairs.length} par påmeldt, men trenger minimum {event.minPairs} par for å generere ruter.
                              </p>
                              <p style={{ color: '#F59E0B', fontSize: '14px', fontWeight: 'bold', marginBottom: '16px' }}>
                                ⚠️ Du trenger {event.minPairs - pairs.length} par til
                              </p>
                            </>
                          ) : (
                            <>
                              <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                                Beregn optimale ruter for alle {pairs.length} par basert på adresser, preferanser og dietthensyn.
                              </p>
                              <button 
                                className="btn btn-primary" 
                                onClick={() => setShowGenerateRoutesModal(true)}
                              >
                                🚴 Generer ruter
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Messaging Section */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ marginBottom: '16px' }}>📧 Kommunikasjon</h3>
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#F9FAFB', 
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#111827' }}>
                        Send melding til sykkelfesterne
                      </h4>
                      <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                        Send praktisk informasjon, oppmuntring eller viktige beskjeder til alle {pairs.length * 2} påmeldte deltakere.
                      </p>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => setShowMessageModal(true)}
                      >
                        📧 Send melding til sykkelfesterne
                      </button>
                    </div>
                  </div>
                  
                  {/* Statistics Section */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ marginBottom: '16px' }}>📊 Statistikk</h3>
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#F9FAFB', 
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#111827' }}>
                        Generer anonymisert statistikk
                      </h4>
                      <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                        Lagre statistikk for dette arrangementet (antall deltakere, par, sykkelkilometer, meldinger osv.). 
                        Statistikken lagres permanent og blir bevart selv om arrangementet eller brukere slettes senere. Statistikken er anonymisert og inneholder ingen personopplysninger.
                        Statistikken vil etter gjennomført arrangement også fanges automatisk, og den kan brukes til å forbedre fremtidige arrangementer og for intern analyse.
                      </p>
                      <button 
                        className="btn btn-primary" 
                        onClick={handleGenerateStatistics}
                        disabled={statisticsLoading}
                      >
                        {statisticsLoading ? '⏳ Genererer...' : '📊 Generer statistikk'}
                      </button>
                      {statisticsMessage && (
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '12px', 
                          backgroundColor: statisticsMessage.includes('Kunne ikke') ? '#FEE2E2' : '#D1FAE5',
                          color: statisticsMessage.includes('Kunne ikke') ? '#991B1B' : '#065F46',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}>
                          {statisticsMessage}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="danger-zone" style={{ marginTop: '32px' }}>
                    <h3 style={{ color: '#EF4444', marginBottom: '16px' }}>⚠️ Farlig sone</h3>
                    
                    {/* Cancel Event Section - Only show if event has participants and is not cancelled */}
                    {pairs.length > 0 && event.status !== 'cancelled' && (
                      <div style={{ 
                        marginBottom: '32px', 
                        padding: '20px', 
                        backgroundColor: '#FEF2F2', 
                        borderRadius: '8px',
                        border: '1px solid #EF4444'
                      }}>
                        <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#991B1B' }}>
                          Avlys arrangement med varsling
                        </h4>
                        <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                          Hvis du trenger å avlyse arrangementet, vil alle {pairs.length * 2} påmeldte deltakere 
                          motta en e-post med forklaring. Arrangementet blir merket som avlyst og vil fortsatt 
                          være synlig i deltakernes portal sammen med e-postloggen.
                        </p>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => setShowCancelModal(true)}
                        >
                          ❌ Avlys arrangement og varsle deltakere
                        </button>
                      </div>
                    )}

                    {/* Show cancelled status */}
                    {event.status === 'cancelled' && (
                      <div style={{ 
                        marginBottom: '32px', 
                        padding: '20px', 
                        backgroundColor: '#FEF3C7', 
                        borderRadius: '8px',
                        border: '1px solid #F59E0B'
                      }}>
                        <h4 style={{ marginTop: 0, marginBottom: '8px', color: '#92400E' }}>
                          ⚠️ Arrangementet er avlyst
                        </h4>
                        <p style={{ margin: 0, color: '#78350F', fontSize: '14px' }}>
                          Dette arrangementet er avlyst. Du kan nå slette det permanent hvis ønskelig.
                        </p>
                      </div>
                    )}

                    {/* Delete Event Section */}
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#FEE2E2', 
                      borderRadius: '8px',
                      border: '1px solid #EF4444'
                    }}>
                      <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#7F1D1D' }}>
                        Slett arrangement permanent
                      </h4>
                      <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                        {pairs.length > 0 && event.status !== 'cancelled' ? (
                          <>
                            <strong style={{ color: '#991B1B' }}>Du kan ikke slette et arrangement med påmeldte deltakere.</strong><br/>
                            Du må først avlyse arrangementet for å varsle alle deltakerne. Etter avlysning kan du slette arrangementet.
                          </>
                        ) : pairs.length > 0 && event.status === 'cancelled' ? (
                          <>
                            Sletter arrangementet permanent inkludert alle påmeldinger, ruter og e-postlogger. 
                            Deltakerne har allerede mottatt avlysningsvarsling.
                          </>
                        ) : (
                          <>
                            Sletter arrangementet permanent. Ingen deltakere er påmeldt, så ingen blir varslet.
                          </>
                        )}
                      </p>
                      <button 
                        className="btn btn-danger" 
                        onClick={handleDelete}
                        disabled={pairs.length > 0 && event.status !== 'cancelled'}
                        style={{
                          opacity: (pairs.length > 0 && event.status !== 'cancelled') ? 0.5 : 1,
                          cursor: (pairs.length > 0 && event.status !== 'cancelled') ? 'not-allowed' : 'pointer'
                        }}
                      >
                        🗑️ Slett arrangement permanent
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Logg Tab */}
              {activeTab === 'logs' && (
                <div className="overview-section">
                  <h2>Logg</h2>
                  <p style={{ marginBottom: '20px', color: '#666' }}>
                    Oversikt over hendelser og varsler for dette arrangementet.
                  </p>

                  {/* Log Sub-tabs */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #E5E7EB' }}>
                    <button
                      onClick={() => setLogSubTab('emails')}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderBottom: logSubTab === 'emails' ? '2px solid var(--primary-color)' : 'none',
                        marginBottom: '-2px',
                        fontWeight: logSubTab === 'emails' ? '600' : 'normal',
                        color: logSubTab === 'emails' ? 'var(--primary-color)' : '#6B7280'
                      }}
                    >
                      📧 E-postvarslinger
                    </button>
                    <button
                      onClick={() => setLogSubTab('system')}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderBottom: logSubTab === 'system' ? '2px solid var(--primary-color)' : 'none',
                        marginBottom: '-2px',
                        fontWeight: logSubTab === 'system' ? '600' : 'normal',
                        color: logSubTab === 'system' ? 'var(--primary-color)' : '#6B7280'
                      }}
                    >
                      ⚙️ Systemlogger
                    </button>
                  </div>

                  {logsLoading ? (
                    <p>Laster logger...</p>
                  ) : (
                    <>
                      {/* Email Logs */}
                      {logSubTab === 'emails' && (
                        logs.filter(log => log.type || log.subject).length === 0 ? (
                          <p>Ingen e-postvarslinger sendt ennå.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {logs.filter(log => log.type || log.subject).map((log) => {
                        const typeLabels: Record<string, string> = {
                          host_notification: '🍽️ Vertsvarsling',
                          registration_confirmation: '✅ Påmeldingsbekreftelse',
                          organizer_message: '💬 Arrangørmelding',
                          event_cancellation: '❌ Avlysning',
                          event_day_firstStop: '🚴 Først stopp-varsling',
                          event_day_mainCourse: '🚴 Hovedrett-varsling',
                          event_day_dessert: '🚴 Dessert-varsling',
                          event_day_finalDestination: '🚴 Siste stopp-varsling'
                        };
                        const typeLabel = typeLabels[log.type] || log.type;
                        const isUnverified = log.status === 'skipped' && log.errorMessage === 'Email not verified';

                        return (
                        <div
                          key={log.id}
                          style={{
                            padding: '16px',
                            borderRadius: '8px',
                            border: `2px solid ${log.status === 'success' ? '#10B981' : isUnverified ? '#F59E0B' : '#EF4444'}`,
                            backgroundColor: log.status === 'success' ? '#F0FDF4' : isUnverified ? '#FEF3C7' : '#FEF2F2'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div>
                              <strong style={{ color: log.status === 'success' ? '#065F46' : isUnverified ? '#78350F' : '#991B1B' }}>
                                {log.status === 'success' ? '✅ Sendt' : isUnverified ? '⚠️ Ikke Sendt - Epostadresse ikke verifisert' : '❌ Feilet'}
                              </strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                                {new Date(log.timestamp).toLocaleString('nb-NO', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: '#E0E7FF',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}>
                                {typeLabel}
                              </span>
                              {log.course && (
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: log.course === 'appetizer' ? '#DBEAFE' : log.course === 'mainCourse' ? '#FEF3C7' : '#FCE7F3',
                                  fontSize: '13px',
                                  marginLeft: '8px'
                                }}>
                                  {log.course === 'appetizer' ? '🥗 Forrett' : log.course === 'mainCourse' ? '🍽️ Hovedrett' : '🍰 Dessert'}
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ marginBottom: '8px' }}>
                            <strong>Emne:</strong> {log.subject}
                          </div>

                          {log.hostNames && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Verter:</strong> {log.hostNames}
                            </div>
                          )}

                          {log.hostAddress && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Adresse:</strong> {log.hostAddress}
                            </div>
                          )}

                          {log.courseTime && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Tidspunkt:</strong> {log.courseTime}
                            </div>
                          )}

                          <div style={{ marginBottom: '8px' }}>
                            <strong>Mottaker:</strong>
                            <div style={{ marginLeft: '16px', marginTop: '4px', fontSize: '14px', color: '#374151' }}>
                              • {log.recipientName} ({log.recipientEmail})
                              {log.recipientUserId && (
                                <span style={{ fontSize: '12px', color: '#6B7280', marginLeft: '8px' }}>
                                  ID: {log.recipientUserId}
                                </span>
                              )}
                            </div>
                          </div>

                          {log.partnerName && (
                            <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                              Partner: {log.partnerName}
                            </div>
                          )}

                          {log.guestCount !== undefined && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Antall gjester:</strong> {log.guestCount} par ({log.guestCount * 2} personer)
                            </div>
                          )}

                          {log.dietaryRestrictions && log.dietaryRestrictions.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Diettbehov:</strong> {log.dietaryRestrictions.join(', ')}
                            </div>
                          )}

                          {log.messageSubject && (
                            <div style={{ marginBottom: '8px' }}>
                              <strong>Meldingsemne:</strong> {log.messageSubject}
                            </div>
                          )}

                          {log.errorMessage && (
                            <div style={{
                              marginTop: '12px',
                              padding: '8px',
                              backgroundColor: '#FEE2E2',
                              borderRadius: '4px',
                              color: '#991B1B',
                              fontSize: '13px'
                            }}>
                              <strong>Feilmelding:</strong> {log.errorMessage}
                            </div>
                          )}

                          {log.emailBodyPlainText && (
                            <div style={{ marginTop: '16px', borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
                              <button
                                onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--primary-color)',
                                  cursor: 'pointer',
                                  padding: '0',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                {expandedLogId === log.id ? '▼' : '▶'} Se e-postens innhold
                              </button>
                              {expandedLogId === log.id && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '16px',
                                  backgroundColor: '#F9FAFB',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  color: '#374151',
                                  lineHeight: '1.6',
                                  maxHeight: '400px',
                                  overflowY: 'auto'
                                }}>
                                  {log.emailBodyPlainText}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                            })}
                          </div>
                        )
                      )}

                      {/* System Logs */}
                      {logSubTab === 'system' && (
                        logs.filter(log => log.action && !log.type).length === 0 ? (
                          <p>Ingen systemlogger ennå.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {logs.filter(log => log.action && !log.type).map((log) => {
                              const actionLabels: Record<string, string> = {
                                REGISTRATION_CLOSED: '🔒 Påmelding stengt',
                                ROUTES_GENERATED: '🗺️ Ruter generert',
                                EVENT_CONFIRMED: '✅ Arrangement bekreftet',
                                EVENT_ACTIVATED: '🎉 Arrangement aktivert',
                                EVENT_COMPLETED: '🏁 Arrangement fullført'
                              };
                              const actionLabel = log.action ? (actionLabels[log.action] || log.action) : 'Ukjent';
                              const actionColors: Record<string, string> = {
                                REGISTRATION_CLOSED: '#F59E0B',
                                ROUTES_GENERATED: '#3B82F6',
                                EVENT_CONFIRMED: '#10B981',
                                EVENT_ACTIVATED: '#8B5CF6',
                                EVENT_COMPLETED: '#6B7280'
                              };
                              const actionColor = log.action ? (actionColors[log.action] || '#6B7280') : '#6B7280';

                              return (
                                <div
                                  key={log.id}
                                  style={{
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E7EB',
                                    backgroundColor: '#F9FAFB'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                    <div>
                                      <strong style={{ color: actionColor, fontSize: '16px' }}>
                                        {actionLabel}
                                      </strong>
                                      <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                                        {new Date(log.timestamp).toLocaleString('nb-NO', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                  </div>

                                  {log.details && (
                                    <div style={{ marginTop: '12px' }}>
                                      <button
                                        onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: 'var(--primary-color)',
                                          cursor: 'pointer',
                                          padding: '0',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}
                                      >
                                        {expandedLogId === log.id ? '▼' : '▶'} Detaljer
                                      </button>
                                      {expandedLogId === log.id && (
                                        <div style={{
                                          marginTop: '12px',
                                          padding: '16px',
                                          backgroundColor: '#FFFFFF',
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          border: '1px solid #E5E7EB'
                                        }}>
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                            {JSON.stringify(log.details, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Event Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => !cancelLoading && setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Avlys arrangement</h2>
            <p>Skriv en melding til deltakerne om hvorfor arrangementet avlyses:</p>
            <textarea
              value={cancelMessage}
              onChange={(e) => setCancelMessage(e.target.value)}
              placeholder="F.eks: På grunn av dårlig vær må vi dessverre avlyse arrangementet..."
              rows={6}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
              >
                Avbryt
              </button>
              <button
                className="btn btn-danger"
                onClick={handleCancelEvent}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Sender varsler...' : `Avlys og varsle ${pairs.length * 2} deltakere`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Slett arrangement permanent</h2>
            <p style={{ marginBottom: '20px' }}>
              {event?.status === 'cancelled' && pairs.length > 0 ? (
                <>
                  Er du sikker på at du vil slette dette avlyste arrangementet permanent?
                  <br /><br />
                  <strong>Dette vil slette:</strong>
                  <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                    <li>Alle påmeldinger ({pairs.length * 2} deltakere)</li>
                    <li>Genererte ruter</li>
                    <li>Hendelseslogg</li>
                  </ul>
                  <br />
                  <strong style={{ color: '#dc3545' }}>Denne handlingen kan ikke angres.</strong>
                </>
              ) : (
                <>
                  Er du sikker på at du vil slette dette arrangementet permanent?
                  <br /><br />
                  <strong style={{ color: '#dc3545' }}>Denne handlingen kan ikke angres.</strong>
                </>
              )}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Avbryt
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
              >
                Slett permanent
              </button>
            </div>
          </div>
        </div>
      )}

      {showHostNotificationConfirm && (
        <div className="modal-overlay" onClick={() => setShowHostNotificationConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Varsle verter om tildelte måltider</h2>
            <p style={{ marginBottom: '20px' }}>
              Er du sikker på at du vil sende e-post til alle vertene med informasjon om deres tildelte måltid?
              <br /><br />
              <strong>Dette vil sende e-post til:</strong>
              <ul style={{ textAlign: 'left', marginTop: '10px' }}>
                <li>Alle par som er tildelt en rett som vert</li>
                <li>Informasjon om hvilken rett de skal lage</li>
                <li>Navn på gjestene som kommer</li>
                <li>Eventuelle matallergier/diettbehov</li>
              </ul>
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowHostNotificationConfirm(false)}
              >
                Avbryt
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmHostNotifications}
              >
                📧 Send varsler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay" onClick={() => !messageLoading && setShowMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Send melding til sykkelfesterne</h2>
            <p>Send en melding til alle {pairs.length * 2} påmeldte deltakere:</p>
            
            <label style={{ display: 'block', marginTop: '20px', marginBottom: '8px', fontWeight: 'bold' }}>
              Emne:
            </label>
            <input
              type="text"
              value={messageSubject}
              onChange={(e) => setMessageSubject(e.target.value)}
              placeholder="F.eks: Viktig informasjon om arrangementet"
              maxLength={100}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                fontFamily: 'inherit'
              }}
            />
            
            <label style={{ display: 'block', marginTop: '16px', marginBottom: '8px', fontWeight: 'bold' }}>
              Melding:
            </label>
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="F.eks: Vi gleder oss til lørdag! Husk å ta med godt humør og regnklær..."
              rows={8}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '8px',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            
            <div style={{ background: '#FFF3CD', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', border: '1px solid #FFE69C' }}>
              <strong>💡 Tips:</strong> Bruk denne funksjonen med omhu. Eksempler på passende meldinger:
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Oppfordre til flere påmeldinger hvis dere er få</li>
                <li>Viktig praktisk informasjon om arrangementet</li>
                <li>Værvarsel eller andre forhold deltakerne bør vite om</li>
                <li>Påminnelse om viktige datoer eller tidsfrister</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowMessageModal(false)}
                disabled={messageLoading}
              >
                Avbryt
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSendMessage}
                disabled={messageLoading || !messageSubject.trim() || !messageContent.trim()}
              >
                {messageLoading ? 'Sender...' : `📧 Send til ${pairs.length * 2} deltakere`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Routes Modal */}
      {showGenerateRoutesModal && (
        <div className="modal-overlay" onClick={() => !generateRoutesLoading && setShowGenerateRoutesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🚴 Generer ruter for Sykkelfest</h2>
            
            {routeErrors.length === 0 && routeWarnings.length === 0 && (
              <>
                <p>Dette vil beregne optimale ruter for alle {pairs.length} par basert på:</p>
                <ul style={{ marginTop: '12px', marginBottom: '20px', paddingLeft: '24px', lineHeight: '1.8' }}>
                  <li>📍 Adresser og avstand til sluttdestinasjon</li>
                  <li>🍽️ Preferanser for hvilken rett hvert par ønsker å lage</li>
                  <li>⚠️ Matallergier og dietthensyn</li>
                  <li>👥 Sikre at ingen par møter hverandre mer enn én gang</li>
                  <li>🎯 Minimere total sykkelavstand</li>
                </ul>
                
                <div style={{ background: '#DBEAFE', padding: '16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', border: '1px solid #93C5FD' }}>
                  <strong>ℹ️ Slik fungerer algoritmen:</strong>
                  <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                    <li>Parene deles inn i grupper på 3 (1 vert + 2 gjester per rett)</li>
                    <li>Forrett plasseres lengst unna sluttdestinasjon (for å sykle innover mot sentrum)</li>
                    <li>Dessert plasseres nærmest sluttdestinasjon (kort vei til etterfest)</li>
                    <li>Algoritmen matcher verter og gjester basert på matallergier</li>
                  </ul>
                </div>

                {event?.minPairs && pairs.length < event.minPairs && (
                  <div style={{ background: '#FEF3C7', padding: '16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', border: '2px solid #F59E0B' }}>
                    <strong>⚠️ Advarsel:</strong> Du har kun {pairs.length} par, men trenger minimum {event.minPairs} par 
                    for at algoritmen skal fungere optimalt. Rutene kan ikke genereres før du har nok påmeldte.
                  </div>
                )}
              </>
            )}

            {routeErrors.length > 0 && (
              <div style={{ background: '#FEE2E2', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #EF4444' }}>
                <strong style={{ color: '#991B1B' }}>❌ Feil ved generering:</strong>
                <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px', color: '#991B1B' }}>
                  {routeErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {routeWarnings.length > 0 && (
              <div style={{ background: '#FEF3C7', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #F59E0B' }}>
                <strong style={{ color: '#92400E' }}>⚠️ Advarsler:</strong>
                <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px', color: '#92400E', fontSize: '14px' }}>
                  {routeWarnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowGenerateRoutesModal(false);
                  setRouteErrors([]);
                  setRouteWarnings([]);
                }}
                disabled={generateRoutesLoading}
              >
                Avbryt
              </button>
              <button
                className="btn btn-primary"
                onClick={handleGenerateRoutes}
                disabled={generateRoutesLoading || (event?.minPairs && pairs.length < event.minPairs) || routeErrors.length > 0}
              >
                {generateRoutesLoading ? '⏳ Beregner ruter...' : '🚴 Generer ruter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEvent;
